/**
 * Workflow Schedules Page
 * Manage scheduled workflow executions and system task schedules
 */

import { useState, useCallback } from 'react';
import { ScheduleList, ScheduleDialog, ExecutionHistoryPanel } from '@/features/workflow-schedules';
import type { WorkflowSchedule } from '@/types/workflowSchedule';
import type { SystemTaskSchedule, ScheduleCategory } from '@/types/systemTaskSchedule';

type Schedule = WorkflowSchedule | SystemTaskSchedule;

// History view state type
interface HistoryViewState {
  scheduleId: string;
  scheduleName?: string;
  category: ScheduleCategory;
}

export function WorkflowSchedulesPage() {
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ScheduleCategory | undefined>(undefined);

  // History panel state
  const [historyView, setHistoryView] = useState<HistoryViewState | null>(null);

  // Handle create
  const handleCreate = useCallback((category?: ScheduleCategory) => {
    setSelectedSchedule(null);
    setSelectedCategory(category);
    setDialogOpen(true);
  }, []);

  // Handle edit
  const handleEdit = useCallback((schedule: Schedule, category: ScheduleCategory) => {
    setSelectedSchedule(schedule);
    setSelectedCategory(category);
    setDialogOpen(true);
  }, []);

  // Handle view history - now receives schedule info from ScheduleList
  const handleViewHistory = useCallback((scheduleId: string, scheduleName?: string, category?: ScheduleCategory) => {
    setHistoryView({
      scheduleId,
      scheduleName,
      category: category || 'workflow',
    });
  }, []);

  // Handle dialog close
  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedSchedule(null);
      setSelectedCategory(undefined);
    }
  }, []);

  return (
    <div className="h-[calc(100vh-4rem)]">
      {/* Main content */}
      {!historyView ? (
        <div className="h-full overflow-y-auto p-6 bg-gray-50">
          <ScheduleList
            onCreate={handleCreate}
            onEdit={handleEdit}
            onViewHistory={handleViewHistory}
          />
        </div>
      ) : (
        <ExecutionHistoryPanel
          scheduleId={historyView.scheduleId}
          scheduleName={historyView.scheduleName}
          category={historyView.category}
          onBack={() => setHistoryView(null)}
        />
      )}

      {/* Create/Edit Dialog */}
      <ScheduleDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        schedule={selectedSchedule}
        scheduleCategory={selectedCategory}
      />
    </div>
  );
}
